import type { UserWithLabels } from '@clockwise/clients/users';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UNIT_ACCESS_LABEL } from '@/lib/business-units';

interface Props {
  /** Primary employer first (the server's order). */
  units: UserWithLabels['businessUnits'];
}

/**
 * An employee's Business Units in a list cell: their primary employer, then
 * "+N" for the other units they work in — every one named, with how they
 * reach it, on hover.
 */
export function PersonUnitsCell({ units }: Props) {
  const [first, ...others] = units;
  if (!first) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="flex flex-wrap items-center gap-1 text-sm">
      <span className="break-words">{first.name}</span>
      {others.length > 0 ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="secondary"
                className="cursor-default px-1.5 tabular-nums"
                aria-label={`${others.length} more Business Units`}
              >
                +{others.length}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <ul className="space-y-0.5">
                {units.map((u) => (
                  <li key={u.id}>
                    <span className="font-medium">{u.name}</span>{' '}
                    <span className="opacity-80">· {UNIT_ACCESS_LABEL[u.access]}</span>
                  </li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : null}
    </span>
  );
}
